<template>
  <div class="py-6 px-4 pb-24 space-y-6" dir="rtl">
    <div class="max-w-2xl mx-auto space-y-6">
      
      <!-- Header -->
      <div class="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-2xl p-6 text-white">
        <h1 class="text-2xl font-bold mb-2">امضای مشتری</h1>
        <p class="text-indigo-100 text-sm">لطفاً امضای خود را در کادر زیر قرار دهید</p>
      </div>

      <!-- Request Info Cards -->
      <div class="space-y-3">
        <!-- Request Name Card -->
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex items-start gap-3">
            <label class="text-sm font-bold text-gray-700">{{$t('requestNameQ')}}</label>
            <span class="text-gray-900 flex-1 text-left">{{request_info.RequestName}}</span>
          </div>
        </div>

        <!-- Status Card -->
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex items-start gap-3">
            <label class="text-sm font-bold text-gray-700">{{$t('statusQ')}}</label>
            <span class="text-gray-900 flex-1 text-left">{{request_info.LastStatus}}</span>
          </div>
        </div>

        <!-- Receipt Expert Card -->
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex items-start gap-3">
            <label class="text-sm font-bold text-gray-700">{{$t('receiptExpert')}}</label>
            <span class="text-gray-900 flex-1 text-left">{{request_info.ExpertName}}</span>
          </div>
        </div>

        <!-- Device Password Card -->
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex items-start gap-3">
            <label class="text-sm font-bold text-gray-700">{{$t('devicePasswordQ')}}</label>
            <span class="text-gray-900 flex-1 text-left">{{request_info.DevicePass}}</span>
          </div>
        </div>

        <!-- Appearance Problem Card -->
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex items-start gap-3">
            <label class="text-sm font-bold text-gray-700">{{ $t('apearenceProblem') }}</label>
            <span class="text-gray-900 flex-1 text-left">{{request_info.AppearanceProblem}}</span>
          </div>
        </div>

        <!-- Phone No Card -->
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex items-start gap-3">
            <label class="text-sm font-bold text-gray-700">{{ $t('phoneNoQ') }}</label>
            <span class="text-gray-900 flex-1 text-left">{{request_info.CustomerMobile}}</span>
          </div>
        </div>

        <!-- Expert Opinion Card -->
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex items-start gap-3">
            <label class="text-sm font-bold text-gray-700">{{ $t('expertOpenionQ') }}</label>
            <span class="text-gray-900 flex-1 text-left">{{request_info.ExpertOpinion}}</span>
          </div>
        </div>

        <!-- Predefined Tags Card -->
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex items-start gap-3">
            <label class="text-sm font-bold text-gray-700">{{ $t('predefinedTagsQ') }}</label>
            <div class="flex-1 flex flex-wrap gap-2">
              <span 
                v-for="item in request_info.PredefinedGlobal"
                :key="item.Text"
                class="px-3 py-1 bg-indigo-500 text-white rounded-lg text-sm font-medium"
              >
                {{item.Text}}:{{item.Value}}
              </span>
              <span 
                v-for="item in request_info.PredefinedChecked"
                :key="item.Text"
                class="px-3 py-1 bg-indigo-500 text-white rounded-lg text-sm font-medium"
              >
                {{item.Text}}:{{item.Value}}
              </span>
            </div>
          </div>
        </div>

        <!-- Price Range Card -->
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex items-start gap-3">
            <label class="text-sm font-bold text-gray-700">{{ $t('priceRangeQ') }}</label>
            <span class="text-gray-900 flex-1 text-left">{{request_info.TechPriceRange}}</span>
          </div>
        </div>
      </div>

      <!-- Attachments Section -->
      <div v-if="attachment_items.length > 0" class="bg-white rounded-lg border border-gray-200 p-6">
        <h3 class="text-lg font-bold text-gray-800 mb-4">پیوست‌های درخواست</h3>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div 
            v-for="item in attachment_items" 
            :key="item.Id"
            class="bg-gray-50 rounded-xl border border-gray-200 p-3 hover:shadow-md transition-shadow cursor-pointer"
            @click="open_image(item.ImagePath)"
          >
            <div class="aspect-square rounded-lg overflow-hidden bg-white mb-2">
              <img 
                :src="item.ImagePath" 
                :alt="item.ImageName" 
                class="w-full h-full object-cover"
                @error="handleImageError"
              />
            </div>
            <p class="text-xs font-medium text-gray-700 truncate text-center">{{ item.ImageName }}</p>
          </div>
        </div>
      </div>

      <!-- Signature Pad -->
      <div class="bg-white rounded-lg border border-gray-200 p-6">
        <h3 class="text-sm font-bold text-gray-700 mb-4 text-center">محل امضا</h3>
        <div class="border border-gray-300 rounded-lg overflow-hidden bg-white aspect-square    mx-auto">
          <VueSignaturePad v-if="showSignaturePad" ref="signaturePad" :options="signature_options" />
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-4">
        <button 
          v-on:click="save()" 
          class="flex-1 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-3 px-6 rounded-xl font-bold hover:shadow-xl transform hover:scale-102 transition-all"
        >
          {{ $t('save') }}
        </button>
        <button 
          v-on:click="undo()" 
          class="flex-1 bg-gray-200 text-gray-700 py-3 px-6 rounded-xl font-bold hover:bg-gray-300 transition-all"
        >
          {{ $t('clear') }}
        </button>
      </div>

    </div>
  </div>
</template>

<script>
  import Vue from 'vue'

  import VueSignaturePad from 'vue-signature-pad'
  import { bus } from '../../main'

  Vue.use(VueSignaturePad)
  export default {
    name: 'MySignaturePad',
    data () {
      return {
        signature_options: {
          dotSize: (0.5 + 2.5) / 2,
          minWidth: 0.5,
          maxWidth: 2.5,
          throttle: 16,
          minDistance: 5,
          backgroundColor: 'rgb(255,255,255)',
          penColor: 'black',
          velocityFilterWeight: 0.7,
          onBegin: () => {
          },
          onEnd: () => {
          }
        },
        req_type:4,
        request_info: [],
        attachment_items: [],
        showSignaturePad: false,
      }
    },
mounted () {
      this.req_type = this.$route.params.type_id
      this.get_request_info()
      this.get_attachs()
      // Load signature pad with 5ms delay
      setTimeout(() => {
        this.showSignaturePad = true
      }, 500)
    },
    methods: {
      undo () {
        this.$refs.signaturePad.clearSignature()
      },
      save () {
        const { isEmpty, data } = this.$refs.signaturePad.saveSignature('image/png')
        // var data_image = this.$refs.signaturePad.getPropImagesAndCacheImages()
        // console.log(this.$refs.signaturePad.getPropImagesAndCacheImages());
        // console.log(data);
        // Usage example:
        var file = this.dataURLtoFile(data, 'signature.png')
        console.log(file)
        // this.$refs.signaturePad.fromData(data)
        this.upload_image(file)
      },
      upload_image (image_data) {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        console.log()
        var form_data = new FormData()
        form_data.append('UserId', user_id)
        form_data.append('ProcessId', this.$route.params.rec_id)
        form_data.append('Format', 'png')
        form_data.append('ReqType', this.req_type)
        form_data.append('Picture', image_data)

        //
        // const send_data = {
        //   "UserId": user_id,
        // };
        // this.axios.post('/Register' , send_data,{
        this.axios.post('UploadFile', form_data, {})
          .then(response => {
self.send_signature(response.data.ArchiveId, response.data.ImageName)
          }).catch(e => {
          this.errors.push(e)
        })
      },
      dataURLtoFile (dataurl, filename) {
    var arr = dataurl.split(',')
      var mime = arr[0].match(/:(.*?);/)[1]
      var bstr = atob(arr[1])
      var n = bstr.length
      var u8arr = new Uint8Array(n)

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }

    return new File([u8arr], filename, { type:mime })
  },

      send_signature (archive_id, file_name) {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        console.log()

        var sign_type = 0

        if (this.req_type == 4) {
          sign_type = 1
        }
        if (this.req_type == 5) {
          sign_type = 2
        }

        const send_data = {
          UserId: user_id,
          ProcessId: this.$route.params.rec_id,
          ImageName: file_name,
          ArchiveId: archive_id,
          SigType: sign_type,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/SetRequestSignature', send_data, {})
          .then(response => {
           if (response.data.error == 0) {
             // self.$router.push({name: 'attachment', params: {'rec_id': self.$route.params.rec_id}})
           if (self.req_type == 4 || self.req_type == 5) {
             self.$router.push({ name: 'requestAddress', params: { rec_id: self.$route.params.rec_id } })
           }
           } else {
             bus.$emit('show_toast', [1500, self.$t('errorUploadSign')])
           }
          }).catch(e => {
          this.errors.push(e)
        })
      },
      get_request_info () {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          ExpertId: user_id,
          RequestId: this.$route.params.rec_id,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/GetRequestInfoByReqId', send_data, {})
          .then(response => {
            if (response.data.error == 0) {
              this.request_info = response.data.rec
            }
            // this.$router.push({ path: "/post-login", params: {} });
          }).catch(e => {
          this.errors.push(e)
        })
      },
      get_attachs () {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          UserId: user_id,
          ReqId: this.$route.params.rec_id,
          IsFinalize: 1,
        }
        this.axios.post('/GetUserRequestImages', send_data, {})
          .then(response => {
            if (response.data.error == 0) {
              self.attachment_items = response.data.results || [];
              // Add https:// prefix if not present
              self.attachment_items.forEach(function(item) {
                if (item.ImagePath && !item.ImagePath.startsWith('http')) {
                  item.ImagePath = 'https://' + item.ImagePath;
                }
              });
            }
          }).catch(e => {
          this.errors.push(e)
        })
      },
      open_image(img) {
        window.open(img, '_blank')
      },
      handleImageError(event) {
        event.target.src = '/img/default-image.png'
      },

    }
  }
</script>
<style>

  </style>